import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { MEDICAL_DEPLOYMENT_LABELS } from "constants/dashboardTerminology";
import { getEstadoOperativoVisual, getOperationalOriginLabel } from "utils/operationalStatus";

const DEFAULT_ALLOWED_ROLES = ["1", "2", "6", "8"];

const getStoredRole = () => {
  try {
    return String(JSON.parse(localStorage.getItem("token") || "{}")?.rol || "");
  } catch (error) {
    return "";
  }
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/);
  if (!match) return String(value);

  const date = `${match[3]}/${match[2]}/${match[1]}`;
  return match[4] ? `${date} ${match[4]}:${match[5]}` : date;
};

const getRequestError = (requestError) => {
  const status = requestError?.response?.status;

  if (status === 401) return "No fue posible autenticar la solicitud.";
  if (status === 403) return "No tiene permiso para consultar el historial de este despliegue.";
  if (status === 404) return "El despliegue no fue encontrado.";
  return "No se pudo consultar el historial de estados.";
};

const HistorialEstadoOperativoDialog = ({
  open,
  onClose,
  convocatoria,
  allowedRoles = DEFAULT_ALLOWED_ROLES,
}) => {
  const idConvocatoria =
    convocatoria?.idConvocatoria ?? convocatoria?.id_convocatoria ?? convocatoria?.id;
  const currentRole = getStoredRole();
  const canView = allowedRoles.map(String).includes(currentRole);
  const idIsValid = Number.isFinite(Number(idConvocatoria)) && Number(idConvocatoria) > 0;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [headerData, setHeaderData] = useState({});
  const requestRef = useRef(null);

  const loadHistory = useCallback(async () => {
    if (!open || !canView || !idIsValid) return;

    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;

    try {
      setLoading(true);
      setError("");

      const response = await axios.get(
        `/dashboard-operativo/convocatorias/${idConvocatoria}/historial-estados`,
        { signal: controller.signal }
      );
      const payload = response?.data?.data || response?.data || {};

      setItems(Array.isArray(payload.items) ? payload.items : []);
      setHeaderData(payload.convocatoria || {});
    } catch (requestError) {
      if (requestError?.code !== "ERR_CANCELED" && requestError?.name !== "CanceledError") {
        setError(getRequestError(requestError));
        setItems([]);
      }
    } finally {
      if (requestRef.current === controller) {
        if (!controller.signal.aborted) setLoading(false);
        requestRef.current = null;
      }
    }
  }, [canView, idConvocatoria, idIsValid, open]);

  useEffect(() => {
    if (!open) {
      requestRef.current?.abort();
      requestRef.current = null;
      setItems([]);
      setHeaderData({});
      setError("");
      setLoading(false);
      return undefined;
    }

    loadHistory();

    return () => {
      requestRef.current?.abort();
      requestRef.current = null;
    };
  }, [loadHistory, open]);

  const deploymentName =
    convocatoria?.descripcionConvo ||
    convocatoria?.nombreJornada ||
    convocatoria?.nombre_jornada ||
    `${MEDICAL_DEPLOYMENT_LABELS.singularShort} sin nombre`;
  const projectName =
    convocatoria?.nombreProyecto ||
    convocatoria?.nombre_proyecto ||
    convocatoria?.codigoProyecto ||
    convocatoria?.codigo_proyecto ||
    headerData.idProyecto ||
    "Sin proyecto";

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 7 }}>
        <Typography variant="h4" component="div" sx={{ fontWeight: 900 }}>
          Historial de estados operativos
        </Typography>
        <Tooltip title="Cerrar">
          <span>
            <IconButton
              aria-label="Cerrar historial de estados"
              onClick={onClose}
              disabled={loading}
              sx={{ position: "absolute", right: 12, top: 12 }}
            >
              <CloseOutlinedIcon />
            </IconButton>
          </span>
        </Tooltip>
      </DialogTitle>

      <DialogContent dividers>
        {!canView ? (
          <Alert severity="warning">No tiene permiso para consultar este historial.</Alert>
        ) : !idIsValid ? (
          <Alert severity="error">No se encontró el despliegue seleccionado.</Alert>
        ) : (
          <Stack spacing={2.5}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 900 }}>
                  {deploymentName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {projectName}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={loading ? <CircularProgress size={18} /> : <RefreshOutlinedIcon />}
                onClick={loadHistory}
                disabled={loading}
              >
                Actualizar historial
              </Button>
            </Box>

            {error && <Alert severity="error">{error}</Alert>}

            {loading && items.length === 0 ? (
              <Box minHeight={220} display="flex" justifyContent="center" alignItems="center">
                <CircularProgress />
              </Box>
            ) : items.length === 0 && !error ? (
              <Alert severity="info">Este despliegue todavía no tiene cambios registrados.</Alert>
            ) : (
              <Stack spacing={1.5} divider={<Divider flexItem />}>
                {items.map((item, index) => {
                  const previousState = item.estadoAnterior;
                  const nextState = item.estadoNuevo;
                  const previousVisual = getEstadoOperativoVisual(previousState);
                  const nextVisual = getEstadoOperativoVisual(nextState);

                  return (
                    <Paper
                      key={item.id || `${item.fechaCambio}-${index}`}
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderLeft: `5px solid ${nextVisual.border}`,
                        bgcolor: nextVisual.background,
                      }}
                    >
                      <Stack spacing={1.25}>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          {previousState ? (
                            <Chip
                              label={item.estadoAnteriorLabel || previousVisual.label}
                              color={previousVisual.chipColor}
                              size="small"
                              variant="filled"
                            />
                          ) : (
                            <Typography variant="body2" sx={{ fontWeight: 800 }}>
                              Estado inicial
                            </Typography>
                          )}
                          <ArrowForwardOutlinedIcon fontSize="small" color="action" />
                          <Chip
                            label={item.estadoNuevoLabel || nextVisual.label}
                            color={nextVisual.chipColor}
                            size="small"
                            variant="filled"
                          />
                        </Box>

                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                          {formatDateTime(item.fechaCambio)} · {getOperationalOriginLabel(item.origen)}
                        </Typography>
                        <Box>
                          <Typography variant="body2">{item.nombreUsuario || "Usuario no disponible"}</Typography>
                          {item.emailUsuario && (
                            <Typography variant="caption" color="text.secondary">
                              {item.emailUsuario}
                            </Typography>
                          )}
                        </Box>
                        {item.motivo && (
                          <Typography variant="body2" color="text.secondary">
                            Motivo: {item.motivo}
                          </Typography>
                        )}
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

HistorialEstadoOperativoDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  convocatoria: PropTypes.object,
  allowedRoles: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
};

export default HistorialEstadoOperativoDialog;
